import {AfterViewInit, Component, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {CatalogService} from '@service/catalog/catalog.service';
import {CommonService} from '@service/common/common.service';
import {HeaderStep} from '@enum/header.step';
import {HeaderService} from '@service/header/header.service';
import {OrderService} from '@service/order/order.service';
import {switchMap, takeUntil, tap} from 'rxjs/operators';
import {EMPTY, Observable, Subject} from 'rxjs';
import {ModelService} from '@service/model/model.service';
import {ProductModel} from '@interface/product-model/product-model';
import {Location} from '@angular/common';
import {PasswordHelperService} from '@service/password-helper/password-helper.service';
import {Order} from '@interface/order/order/order';
import {ModalService} from '@service/modal/modal.service';
import {AuthService} from '@service/auth/auth.service';

@Component({
    selector: 'app-catalog-list',
    templateUrl: './catalog-list.component.html',
    styleUrls: ['./catalog-list.component.scss'],
    standalone: false
})
export class CatalogListComponent implements AfterViewInit, OnDestroy {
  private destroy$ = new Subject();
  public catalogs?: {
    categoryId: number,
    categoryName: string,
    categoryCode: string,
    productModels: ProductModel[]
  }[];
  public overflow = true;
  public loaded = false;
  public selectedId = 0;

  constructor(private router: Router, private catalogService: CatalogService,
              private orderService: OrderService, private common: CommonService,
              private headerService: HeaderService, private modelService: ModelService,
              private location: Location, private modalService: ModalService,
              private authService: AuthService,
              private activatedRoute: ActivatedRoute, private passwordHelper: PasswordHelperService) {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((e) => {
      if (
        'url' in e && e.url &&
        e.url !== this.router.url &&
        !/^(\/model-configuration\/\d+)$/.test(e.url)
      ) {
        this.modelService.resetPreviousFilter();
      }
    });
    this.passwordHelper
      .handleHelper(this.activatedRoute.url, this.activatedRoute.queryParams)
      .pipe(
        switchMap((modal) => modal?.beforeClosed() || EMPTY),
        takeUntil(this.destroy$)
      ).subscribe((res) => {
      if (!res) {
        this.location.replaceState('/catalog-list');
      }
    });
    this.activatedRoute.params.pipe(
      tap((params: any) => {
        if (params?.id) {
          this.openOrder(+params?.id);
        }
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  ngAfterViewInit(): void {
    this.headerService.header.next(HeaderStep.HOME);
    this.searchCatalogs();
    this.selectedId = 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  public productIndex(): void {
    this.router.navigate(['catalog-index']);
  }

  public catalogList(): void {
    this.router.navigate(['catalog-list']);
  }

  public searchCatalogs(): void {
    this.catalogService.categoryList({
      withLoadedModels: 4
    })
      .pipe(
        tap((data: any) => {
          if (data.error) {
            this.common.showErrorToast(data.error);
          } else {
            if (!this.catalogs || this.catalogs?.length === 0) {
              this.catalogs = data.categories;
            } else if (data.categories?.length > 0) {
              this.catalogs = [...this.catalogs, ...data.categories];
            } else {
              this.overflow = false;
            }
            this.loaded = true;
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  public convertProductImage(model: ProductModel, skip?: boolean): string {
    return this.common.convertProductImage(model, skip);
  }

  public modelDetail(modelId: number, categoryId: number): void {
    if (modelId) {
      this.headerService.header.next(HeaderStep.MODEL_TECHNICAL);
      this.router.navigate(['model-detail-grid', String(modelId), String(categoryId)]);
    }
  }

  public catalogDetail(categoryId: number): void {
    this.router.navigate(['catalog-detail', String(categoryId)]);
  }

  public goToHome(): void {
    this.router.navigate(['catalog-list']);
  }

  public imageLoaded(model: ProductModel): void {
    model.imageLoaded = true;
  }

  public shouldIndexBeVisible(i: number): boolean {
    return i < 4;
  }

  public isCopyingConfiguration(): boolean {
    return !!this.orderService.copyConfigurationId;
  }

  public cancelCopyingConfiguration(): void {
    this.orderService.copyConfigurationId = null;
  }

  public openOrder(orderId: number): void {
    if (this.authService.isUserAuthenticated) {
      this.orderService.orderDetail({orderId}).pipe(
        switchMap((order: Order | { error: any }) => {
          if (('error' in order)) {
            this.common.showErrorToast(order.error?.data?.message || order.error);
            return EMPTY;
          } else {
            return this.orderService.backupAndViewOrder(order).pipe(
              switchMap(() => this.openOrderDetail(order)),
            );
          }
        }),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.goToHome();
      });
    } else {
      this.authService.goToLoginPage();
    }
  }

  public openOrderDetail(order: Order): Observable<{ shouldUpdateListWhenClose: boolean } | undefined> {
    const dialogRef = this.modalService.openOrderModal({
      showSelectedOrder: order,
      isToRevertConfiguration: true
    });
    return dialogRef.afterClosed();
  }

}
